﻿import React, { forwardRef } from 'react';
import { View, Text, StyleSheet } from 'react-native';

export interface WorkoutExercise {
  name: string;
  weight: number;
  reps: number;
  sets?: number;
  isPR?: boolean;
}

export interface WorkoutShareCardProps {
  username: string;
  gymName: string;
  exercises: WorkoutExercise[];
  rank?: number;
  strengthScore?: number;
  storyMode?: boolean;
  /** Unit label (e.g. 'lbs' or 'kg'). Defaults to 'lbs'. */
  unit?: string;
  /** Show watermark for free users */
  showWatermark?: boolean;
}

const WorkoutShareCard = forwardRef<View, WorkoutShareCardProps>(
  ({ username, gymName, exercises, rank, strengthScore, storyMode, unit = 'lbs', showWatermark }, ref) => {
    const dateStr = new Date().toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    // Volume = weight × reps × sets (best-set data, so multiply back by set count)
    const totalVolume = exercises.reduce((sum, e) => sum + e.weight * e.reps * (e.sets ?? 1), 0);
    const prExercises = exercises.filter((e) => e.isPR);

    return (
      <View ref={ref} style={[styles.card, storyMode && styles.cardStory]} collapsable={false}>
        {/* Gold top bar */}
        <View style={styles.goldBar} />

        {/* Big DUEL brand */}
        <View style={styles.brandBlock}>
          <Text style={styles.brandText}>DUEL</Text>
          <View style={styles.brandLine} />
        </View>

        {/* Username — the hero */}
        <Text style={styles.username}>@{username}</Text>
        <Text style={styles.gymName}>{gymName || 'Independent'}</Text>
        {rank != null && (
          <Text style={styles.rankText}>#{rank} AT THIS GYM</Text>
        )}

        {/* Date */}
        <Text style={styles.date}>{dateStr}</Text>

        {/* Stats row */}
        <View style={styles.statsRow}>
          <View style={styles.statCell}>
            <Text style={styles.statNum}>{exercises.length}</Text>
            <Text style={styles.statLabel}>EXERCISES</Text>
          </View>
          <View style={styles.statDiv} />
          <View style={styles.statCell}>
            <Text style={styles.statNum}>{totalVolume.toLocaleString()}</Text>
            <Text style={styles.statLabel}>TOTAL {unit.toUpperCase()}</Text>
          </View>
          {prExercises.length > 0 && (
            <>
              <View style={styles.statDiv} />
              <View style={styles.statCell}>
                <Text style={[styles.statNum, { color: '#B8972E' }]}>
                  {prExercises.length}
                </Text>
                <Text style={[styles.statLabel, { color: '#B8972E' }]}>PRs</Text>
              </View>
            </>
          )}
        </View>

        {/* Exercise list */}
        <View style={styles.exerciseList}>
          {exercises.slice(0, 6).map((ex, i) => (
            <View key={i} style={[styles.exRow, ex.isPR && styles.exRowPR]}>
              <Text style={[styles.exName, ex.isPR && { color: '#D4B44A' }]} numberOfLines={1}>
                {ex.isPR ? '🔥 ' : ''}{ex.name}
              </Text>
              <Text style={styles.exStats}>
                {(ex.sets && ex.sets > 1 ? `${ex.sets}×  ` : '') +
                  ((ex.weight === 0 || !ex.weight) ? 'BW' : `${ex.weight} ${unit}`) +
                  ` × ${ex.reps}` +
                  (ex.isPR ? '  ★' : '')}
              </Text>
            </View>
          ))}
          {exercises.length > 6 && (
            <Text style={styles.moreText}>+{exercises.length - 6} more</Text>
          )}
        </View>

        {/* Strength score */}
        {strengthScore != null && strengthScore > 0 && (
          <View style={styles.scoreRow}>
            <Text style={styles.scoreLabel}>STRENGTH</Text>
            <Text style={styles.scoreVal}>{strengthScore.toLocaleString()}</Text>
          </View>
        )}

        {/* CTA */}
        <View style={styles.ctaBlock}>
          <View style={styles.ctaLine} />
          <Text style={styles.ctaMain}>THINK YOU CAN BEAT THIS?</Text>
          <Text style={styles.ctaSub}>Challenge me on DUEL</Text>
          <View style={styles.ctaLine} />
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerApp}>DOUEVENLIFT.COM</Text>
          <Text style={styles.footerTag}>Competitive Strength Training</Text>
        </View>

        {showWatermark && (
          <View style={styles.watermark} pointerEvents="none">
            <Text style={styles.watermarkText}>DOUEVENLIFT.COM</Text>
          </View>
        )}
      </View>
    );
  },
);

WorkoutShareCard.displayName = 'WorkoutShareCard';
export default WorkoutShareCard;

const styles = StyleSheet.create({
  card: {
    width: 1080,
    minHeight: 1080,
    backgroundColor: '#030303',
    padding: 72,
    paddingTop: 0,
    position: 'relative' as const,
  },
  cardStory: {
    height: 1920,
    minHeight: 1920,
    justifyContent: 'center',
  },

  goldBar: {
    height: 8,
    backgroundColor: '#B8972E',
    marginHorizontal: -72,
    marginBottom: 56,
  },

  brandBlock: {
    alignItems: 'center',
    marginBottom: 48,
  },
  brandText: {
    color: '#E8E4DB',
    fontSize: 96,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 20,
  },
  brandLine: {
    width: 160,
    height: 3,
    backgroundColor: '#B8972E',
    marginTop: 16,
  },

  username: {
    color: '#E8E4DB',
    fontSize: 80,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 2,
    marginBottom: 8,
  },
  gymName: {
    color: '#7A776E',
    fontSize: 36,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 3,
  },
  rankText: {
    color: '#B8972E',
    fontSize: 32,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 2,
    marginTop: 12,
  },
  date: {
    color: '#4A4840',
    fontSize: 24,
    fontFamily: 'Inter_400Regular',
    marginTop: 16,
    marginBottom: 40,
  },

  statsRow: {
    flexDirection: 'row',
    backgroundColor: '#0A0A0A',
    borderWidth: 2,
    borderColor: '#1A1A1A',
    borderRadius: 2,
    paddingVertical: 32,
    marginBottom: 40,
  },
  statCell: {
    flex: 1,
    alignItems: 'center',
  },
  statNum: {
    color: '#E8E4DB',
    fontSize: 56,
    fontFamily: 'Oswald_700Bold',
  },
  statLabel: {
    color: '#4A4840',
    fontSize: 22,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 4,
    marginTop: 4,
  },
  statDiv: {
    width: 2,
    backgroundColor: '#1A1A1A',
  },

  exerciseList: {
    gap: 10,
    marginBottom: 40,
  },
  exRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#0A0A0A',
    borderRadius: 2,
    borderLeftWidth: 5,
    borderLeftColor: '#1A1A1A',
    paddingHorizontal: 28,
    paddingVertical: 24,
  },
  exRowPR: {
    borderLeftColor: '#B8972E',
    backgroundColor: '#0F0D08',
  },
  exName: {
    color: '#E8E4DB',
    fontSize: 32,
    fontFamily: 'Inter_600SemiBold',
    flex: 1,
  },
  exStats: {
    color: '#E8E4DB',
    fontSize: 36,
    fontFamily: 'Oswald_700Bold',
    marginLeft: 24,
  },
  moreText: {
    color: '#4A4840',
    fontSize: 24,
    fontFamily: 'Inter_500Medium',
    textAlign: 'center',
    marginTop: 8,
  },

  scoreRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'baseline',
    gap: 20,
    marginBottom: 32,
  },
  scoreLabel: {
    color: '#4A4840',
    fontSize: 24,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 5,
  },
  scoreVal: {
    color: '#B8972E',
    fontSize: 72,
    fontFamily: 'Oswald_700Bold',
  },

  ctaBlock: {
    alignItems: 'center',
    gap: 16,
    marginTop: 32,
    marginBottom: 40,
  },
  ctaLine: {
    width: 200,
    height: 2,
    backgroundColor: '#B8972E',
  },
  ctaMain: {
    color: '#B8972E',
    fontSize: 40,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 4,
    textAlign: 'center',
  },
  ctaSub: {
    color: '#7A776E',
    fontSize: 28,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 3,
  },

  footer: {
    alignItems: 'center',
  },
  footerApp: {
    color: '#B8972E',
    fontSize: 40,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 8,
  },
  footerTag: {
    color: '#4A4840',
    fontSize: 22,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 3,
    marginTop: 6,
  },
  watermark: {
    position: 'absolute' as const,
    bottom: 32,
    right: 32,
    backgroundColor: 'rgba(0,0,0,0.55)',
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 2,
  },
  watermarkText: {
    color: 'rgba(232,228,219,0.7)',
    fontSize: 36,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 4,
    textTransform: 'uppercase' as const,
  },
});
